import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { programs, type Program } from "@/mocks/programs";

/** Mock episodes for podcast-style content */
interface PodcastEpisode {
  id: number;
  title: string;
  description: string;
  durationMin: number;
  durationLabel: string;
  date: string;
}

function generateEpisodes(program: Program): PodcastEpisode[] {
  const base = program.id * 100;
  return [
    { id: base + 1, title: `#${base + 1} - Lo mejor de la semana`, description: `Resumen de los momentos más destacados de ${program.name}. Entrevistas, debates y lo mejor de la música.`, durationMin: 45, durationLabel: "45 min", date: "2026-07-12" },
    { id: base + 2, title: `#${base + 2} - Entrevista exclusiva`, description: `Conversación a fondo con un invitado especial en ${program.name}. Historias que no te podés perder.`, durationMin: 38, durationLabel: "38 min", date: "2026-07-05" },
    { id: base + 3, title: `#${base + 3} - Especial temático`, description: `Un episodio dedicado a un tema que pidió la comunidad de ${program.name}. Análisis, opinión y participación de oyentes.`, durationMin: 52, durationLabel: "52 min", date: "2026-06-28" },
    { id: base + 4, title: `#${base + 4} - Detrás de escena`, description: `El backstage de ${program.name}. Cómo se prepara cada episodio, anécdotas del equipo y secretos del estudio.`, durationMin: 30, durationLabel: "30 min", date: "2026-06-21" },
    { id: base + 5, title: `#${base + 5} - Mejores momentos del mes`, description: `Compilado con los highlights del último mes en ${program.name}. Risas, emoción y mucho contenido.`, durationMin: 42, durationLabel: "42 min", date: "2026-06-14" },
    { id: base + 6, title: `#${base + 6} - Mesa redonda`, description: `Debate con varios invitados en ${program.name}. Distintas perspectivas sobre un tema de actualidad.`, durationMin: 55, durationLabel: "55 min", date: "2026-06-07" },
    { id: base + 7, title: `#${base + 7} - Preguntas de oyentes`, description: `Respondemos las preguntas que mandaron los oyentes de ${program.name}. Sin filtro, sin edición.`, durationMin: 35, durationLabel: "35 min", date: "2026-05-31" },
    { id: base + 8, title: `#${base + 8} - Piloto`, description: `El primer episodio de ${program.name} como podcast. El inicio de una nueva etapa para el programa.`, durationMin: 40, durationLabel: "40 min", date: "2026-05-24" },
  ];
}

export default function TvPodcastPage() {
  const navigate = useNavigate();
  const { podcastId } = useParams<{ podcastId: string }>();
  const [program, setProgram] = useState<Program | null>(null);
  const [episodes, setEpisodes] = useState<PodcastEpisode[]>([]);
  const [currentEpisode, setCurrentEpisode] = useState<PodcastEpisode | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [focusedBtn, setFocusedBtn] = useState<string>("play");
  const [focusedEpisodeIdx, setFocusedEpisodeIdx] = useState<number | null>(null);

  const buttons = ["back", "prevEp", "play", "nextEp"] as const;

  // Load program
  useEffect(() => {
    if (!podcastId) return;
    const numericId = Number(podcastId);
    if (isNaN(numericId)) return;
    const found = programs.find((p) => p.id === numericId);
    if (found) {
      setProgram(found);
      const eps = generateEpisodes(found);
      setEpisodes(eps);
      setCurrentEpisode(eps[0]);
    }
  }, [podcastId]);

  // Reset progress when episode changes
  useEffect(() => {
    setProgress(0);
    setIsPlaying(false);
  }, [currentEpisode]);

  // Progress simulation
  useEffect(() => {
    if (!isPlaying || !currentEpisode) return;
    const totalMs = currentEpisode.durationMin * 60 * 1000;
    const interval = setInterval(() => {
      setProgress((prev) => {
        const next = prev + 1000 / totalMs;
        if (next >= 1) {
          setIsPlaying(false);
          return 1;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isPlaying, currentEpisode]);

  const togglePlay = useCallback(() => {
    if (progress >= 1) {
      setProgress(0);
    }
    setIsPlaying((prev) => !prev);
  }, [progress]);

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    setProgress(ratio);
  }, []);

  const prevEpisode = useCallback(() => {
    if (!currentEpisode) return;
    const idx = episodes.findIndex((ep) => ep.id === currentEpisode.id);
    if (idx > 0) setCurrentEpisode(episodes[idx - 1]);
  }, [currentEpisode, episodes]);

  const nextEpisode = useCallback(() => {
    if (!currentEpisode) return;
    const idx = episodes.findIndex((ep) => ep.id === currentEpisode.id);
    if (idx < episodes.length - 1) setCurrentEpisode(episodes[idx + 1]);
  }, [currentEpisode, episodes]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        if (focusedEpisodeIdx !== null) {
          setFocusedEpisodeIdx(Math.min((focusedEpisodeIdx || 0) + 1, episodes.length - 1));
        } else {
          const idx = buttons.indexOf(focusedBtn as typeof buttons[number]);
          setFocusedBtn(buttons[(idx + 1) % buttons.length]);
        }
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (focusedEpisodeIdx !== null) {
          setFocusedEpisodeIdx(Math.max((focusedEpisodeIdx || 0) - 1, 0));
        } else {
          const idx = buttons.indexOf(focusedBtn as typeof buttons[number]);
          setFocusedBtn(buttons[(idx - 1 + buttons.length) % buttons.length]);
        }
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        if (focusedEpisodeIdx === null) {
          setFocusedEpisodeIdx(0);
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (focusedEpisodeIdx !== null && focusedEpisodeIdx === 0) {
          setFocusedEpisodeIdx(null);
        }
      } else if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault();
        if (focusedEpisodeIdx !== null) {
          setFocusedEpisodeIdx(null);
        } else {
          navigate("/tv-app");
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (focusedEpisodeIdx !== null && episodes[focusedEpisodeIdx]) {
          setCurrentEpisode(episodes[focusedEpisodeIdx]);
          setFocusedEpisodeIdx(null);
        } else if (focusedBtn === "play") {
          togglePlay();
        } else if (focusedBtn === "back") {
          navigate("/tv-app");
        } else if (focusedBtn === "prevEp") {
          prevEpisode();
        } else if (focusedBtn === "nextEp") {
          nextEpisode();
        }
      } else if (e.key === " ") {
        e.preventDefault();
        togglePlay();
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [focusedBtn, focusedEpisodeIdx, navigate, togglePlay, prevEpisode, nextEpisode, episodes]);

  // Not found
  if (!program || !currentEpisode) {
    return (
      <div className="relative min-h-screen bg-[#0a0a0f] text-white overflow-hidden select-none flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-white/5 flex items-center justify-center mb-6">
            <i className="ri-headphone-line text-white/40 text-3xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Podcast no encontrado</h1>
          <p className="text-white/40 text-sm mb-8">Este podcast no existe o fue eliminado.</p>
          <button onClick={() => navigate("/tv-app")} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold cursor-pointer transition-all outline-none focus:ring-2 focus:ring-white">
            <i className="ri-arrow-left-line mr-2"></i>Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  const progressPct = `${(progress * 100).toFixed(1)}%`;
  const elapsedMin = Math.floor((progress * currentEpisode.durationMin * 60) / 60);
  const elapsedSec = Math.floor((progress * currentEpisode.durationMin * 60) % 60);

  return (
    <div className="relative min-h-screen bg-[#0a0a0f] text-white overflow-hidden select-none">
      {/* Background */}
      <div className="absolute inset-0">
        <img src={program.coverUrl} alt="" className="w-full h-full object-cover object-top opacity-15 blur-xl scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/70 to-[#0a0a0f]/30"></div>
      </div>

      <div className="relative z-10 h-screen flex flex-col">
        {/* Top Bar */}
        <div className="flex items-center justify-between px-6 lg:px-10 py-5">
          <button
            onClick={() => navigate("/tv-app")}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-semibold ${
              focusedBtn === "back" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30" : "bg-white/10 hover:bg-white/15 text-white"
            }`}
          >
            <i className="ri-arrow-left-line text-lg"></i>
            Volver
          </button>
          <div className="flex items-center gap-3">
            {program.isActive && (
              <span className="px-3 py-1.5 rounded-full bg-emerald-500/90 text-white text-xs font-bold flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                ACTIVO
              </span>
            )}
            <span className="px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-bold">{program.category}</span>
          </div>
        </div>

        {/* Main Content: Two columns */}
        <div className="flex-1 flex flex-col lg:flex-row gap-8 px-6 lg:px-10 py-4 overflow-hidden">
          {/* Left: Cover + Controls */}
          <div className="lg:w-[45%] flex flex-col items-center justify-center">
            <div className="w-64 h-64 lg:w-80 lg:h-80 rounded-3xl overflow-hidden ring-4 ring-white/10 shadow-2xl flex-shrink-0">
              <img src={program.coverUrl} alt={program.name} className="w-full h-full object-cover" />
            </div>

            {/* Episode Title */}
            <h1 className="text-2xl lg:text-3xl font-bold text-white mt-6 text-center">{program.name}</h1>
            <p className="text-lg text-white/50 mt-2 text-center">{currentEpisode.title}</p>

            {/* Host Info */}
            <div className="flex items-center gap-3 mt-4">
              {program.hosts[0] && (
                <>
                  <img src={program.hosts[0].photoUrl} alt={program.hosts[0].name} className="w-8 h-8 rounded-full object-cover" />
                  <span className="text-sm text-white/60">{program.hosts[0].name}</span>
                </>
              )}
              <span className="text-sm text-white/30">·</span>
              <span className="text-sm text-white/40">{program.radioName}</span>
            </div>

            {/* Progress Bar */}
            <div className="w-full max-w-md mt-6">
              <div
                className="w-full h-2 rounded-full bg-white/10 cursor-pointer overflow-hidden"
                onClick={handleSeek}
              >
                <div className="h-full bg-violet-400 rounded-full transition-all duration-1000" style={{ width: progressPct }}></div>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-white/50">{elapsedMin}:{String(elapsedSec).padStart(2, "0")}</span>
                <span className="text-xs text-white/50">{currentEpisode.durationLabel}</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => navigate("/tv-app")}
                className={`flex items-center gap-2 px-4 py-3 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-sm font-bold ${
                  focusedBtn === "back" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"
                }`}
              >
                <i className="ri-arrow-left-line text-lg"></i>
              </button>

              <button
                onClick={prevEpisode}
                className={`flex items-center gap-1 px-3 py-3 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-sm font-bold ${
                  focusedBtn === "prevEp" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"
                }`}
              >
                <i className="ri-skip-back-line text-lg"></i>
              </button>

              <button
                onClick={togglePlay}
                className={`flex items-center gap-2 px-8 py-3 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-bold ${
                  focusedBtn === "play"
                    ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105"
                    : isPlaying ? "bg-white text-[#0a0a0f] hover:bg-white/90" : "bg-white/80 text-[#0a0a0f] hover:bg-white"
                }`}
              >
                <i className={`text-lg ${isPlaying ? "ri-pause-fill" : progress >= 1 ? "ri-restart-line" : "ri-play-fill"}`}></i>
                {isPlaying ? "Pausar" : progress >= 1 ? "Repetir" : "Reproducir"}
              </button>

              <button
                onClick={nextEpisode}
                className={`flex items-center gap-1 px-3 py-3 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-sm font-bold ${
                  focusedBtn === "nextEp" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"
                }`}
              >
                <i className="ri-skip-forward-line text-lg"></i>
              </button>
            </div>

            {/* Episode Description */}
            <p className="text-sm text-white/50 mt-5 max-w-md text-center leading-relaxed line-clamp-2">{currentEpisode.description}</p>
          </div>

          {/* Right: Episode List */}
          <div className="lg:w-[55%] overflow-y-auto">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2 sticky top-0 bg-[#0a0a0f]/50 backdrop-blur-sm py-2 z-10">
              <i className="ri-list-check text-violet-400"></i>
              Episodios ({episodes.length})
            </h3>
            <div className="space-y-2 pr-2">
              {episodes.map((ep, idx) => (
                <button
                  key={ep.id}
                  tabIndex={0}
                  onClick={() => { setCurrentEpisode(ep); setFocusedEpisodeIdx(null); }}
                  className={`w-full flex items-start gap-4 p-4 rounded-2xl text-left cursor-pointer transition-all outline-none ${
                    currentEpisode.id === ep.id
                      ? "bg-violet-500/15 border border-violet-500/20"
                      : focusedEpisodeIdx === idx
                      ? "bg-white/5 ring-2 ring-white ring-offset-2 ring-offset-[#0a0a0f]"
                      : "hover:bg-white/5"
                  }`}
                >
                  <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    {currentEpisode.id === ep.id && isPlaying ? (
                      <div className="flex items-end gap-0.5 h-3">
                        {[2, 4, 3, 5, 2].map((h, i) => (
                          <div key={i} className="w-0.5 rounded-full bg-violet-400" style={{ height: `${h * 3}px`, animation: `tv-pod-eq 0.6s ease-in-out infinite`, animationDelay: `${i * 0.1}s` }}></div>
                        ))}
                      </div>
                    ) : (
                      <i className={`${currentEpisode.id === ep.id ? "ri-headphone-fill text-violet-400" : "ri-headphone-line text-white/40"} text-sm`}></i>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${currentEpisode.id === ep.id ? "text-violet-400" : "text-white"}`}>
                      {ep.title}
                    </p>
                    <p className="text-xs text-white/40 mt-0.5 line-clamp-1">{ep.description}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-xs text-white/30">{ep.durationLabel}</span>
                      <span className="text-xs text-white/30">{new Date(ep.date).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes tv-pod-eq {
          0%, 100% { transform: scaleY(0.4); }
          50% { transform: scaleY(1); }
        }
      `}</style>
    </div>
  );
}